import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { canManageInventory, getUserSession } from '../services/authService';
import { fetchInventoryServices, saveInventoryService } from '../services/adminService';
import { fetchAgendaSpecialties, formatAgendaPrecio } from '../services/agendaService';
import '../styles/admin.css';

const emptyForm = {
  id: 0,
  nombre: '',
  caracteristicas: '',
  especialidad_codigo: '',
  tipo_cita: 'terapia',
  costo: '',
  precio: '',
  moneda: 'PAB',
  activo: true,
};

const AdminInventoryPage = () => {
  const history = useHistory();
  const session = getUserSession();
  const sessionId = session?.id ?? null;
  const [services, setServices] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const specialtyLabel = useCallback(
    (codigo) => {
      const match = specialties.find(
        (item) => String(item.codigo) === String(codigo) || String(item.id) === String(codigo)
      );
      return match ? match.label : codigo;
    },
    [specialties]
  );

  const loadData = useCallback(() => {
    if (!sessionId) {
      return Promise.resolve();
    }

    setLoading(true);
    setError('');

    const servicesPromise = fetchInventoryServices(sessionId)
      .then((servicesList) => {
        setServices(servicesList);
      })
      .catch((requestError) => {
        setError(requestError.message || 'No se pudo cargar el inventario.');
      });

    const specialtiesPromise = fetchAgendaSpecialties()
      .then((specialtiesList) => {
        setSpecialties(specialtiesList);
        setForm((current) => {
          if (current.especialidad_codigo || !specialtiesList.length) {
            return current;
          }

          return {
            ...current,
            especialidad_codigo: String(specialtiesList[0].codigo || specialtiesList[0].id),
          };
        });
      })
      .catch(() => {
        setSpecialties([]);
      });

    return Promise.all([servicesPromise, specialtiesPromise]).finally(() => {
      setLoading(false);
    });
  }, [sessionId]);

  useEffect(() => {
    const currentSession = getUserSession();

    if (!currentSession?.id) {
      history.replace('/iniciar-sesion');
      return;
    }

    if (!canManageInventory(currentSession)) {
      history.replace('/');
      return;
    }

    loadData();
  }, [history, loadData]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleEdit = (service) => {
    setMessage('');
    setError('');
    setForm({
      id: service.id,
      nombre: service.nombre || '',
      caracteristicas: service.caracteristicas || '',
      especialidad_codigo: String(service.especialidad_codigo || ''),
      tipo_cita: service.tipo_cita === 'evaluacion' ? 'evaluacion' : 'terapia',
      costo: service.costo != null ? String(service.costo) : '',
      precio: service.precio != null ? String(service.precio) : '',
      moneda: service.moneda || 'PAB',
      activo: Boolean(service.activo),
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!sessionId || saving) {
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');

    saveInventoryService(sessionId, {
      id: form.id,
      nombre: form.nombre.trim(),
      caracteristicas: form.caracteristicas.trim(),
      especialidad_codigo: form.especialidad_codigo,
      tipo_cita: form.tipo_cita,
      costo: form.costo,
      precio: form.precio,
      moneda: form.moneda.trim().toUpperCase(),
      activo: form.activo,
    })
      .then(() => {
        setMessage('Servicio guardado. Ese precio es el que queda en la cita cuando se paga.');
        setForm((current) => ({ ...emptyForm, especialidad_codigo: current.especialidad_codigo }));
        return loadData();
      })
      .catch((requestError) => {
        setError(requestError.message || 'No se pudo guardar el servicio.');
      })
      .finally(() => {
        setSaving(false);
      });
  };

  if (!session?.id || !canManageInventory(session)) {
    return null;
  }

  return (
    <div className="admin-root">
      <section className="admin-section">
        <header className="admin-header">
          <div>
            <h1>Inventario</h1>
            <p>
              Define el costo interno y el precio que ve el paciente, por especialidad y por tipo
              de cita. Al pagar, ese precio queda guardado en la cita para los reportes de ventas.
            </p>
          </div>
          <button
            type="button"
            className="admin-btn admin-btn--secondary"
            onClick={() => {
              setForm((current) => ({
                ...emptyForm,
                especialidad_codigo: current.especialidad_codigo,
              }));
              setMessage('');
              setError('');
            }}
          >
            Nuevo servicio
          </button>
        </header>

        {error ? (
          <div className="admin-alert admin-alert--error" role="alert">
            {error}
          </div>
        ) : null}
        {message ? (
          <div className="admin-alert admin-alert--success" role="status">
            {message}
          </div>
        ) : null}

        <div className="admin-layout">
          <div className="admin-card">
            <form className="admin-form" onSubmit={handleSubmit}>
              <label className="admin-field">
                Nombre del servicio
                <input name="nombre" value={form.nombre} onChange={handleChange} required maxLength={150} />
              </label>
              <label className="admin-field">
                Características
                <textarea
                  name="caracteristicas"
                  value={form.caracteristicas}
                  onChange={handleChange}
                  rows={4}
                />
              </label>
              <label className="admin-field">
                Especialidad
                <select
                  name="especialidad_codigo"
                  value={form.especialidad_codigo}
                  onChange={handleChange}
                  required
                >
                  <option value="">Selecciona</option>
                  {specialties.map((item) => (
                    <option key={item.codigo || item.id} value={String(item.codigo || item.id)}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>
              <div className="admin-form-row">
                <label className="admin-field">
                  Tipo de cita
                  <select name="tipo_cita" value={form.tipo_cita} onChange={handleChange}>
                    <option value="evaluacion">Evaluación</option>
                    <option value="terapia">Terapia</option>
                  </select>
                </label>
                <label className="admin-field">
                  Moneda
                  <input name="moneda" value={form.moneda} onChange={handleChange} maxLength={3} required />
                </label>
              </div>
              <div className="admin-form-row">
                <label className="admin-field">
                  Costo
                  <input
                    name="costo"
                    value={form.costo}
                    onChange={handleChange}
                    inputMode="decimal"
                    required
                  />
                </label>
                <label className="admin-field">
                  Precio
                  <input
                    name="precio"
                    value={form.precio}
                    onChange={handleChange}
                    inputMode="decimal"
                    required
                  />
                </label>
              </div>
              <label className="admin-checkbox">
                <input type="checkbox" name="activo" checked={form.activo} onChange={handleChange} />
                Activo. Es el precio que se muestra en la agenda.
              </label>
              <button type="submit" className="admin-btn" disabled={saving || loading}>
                {saving ? 'Guardando…' : 'Guardar servicio'}
              </button>
            </form>
          </div>

          <div className="admin-card">
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Servicio</th>
                    <th>Especialidad</th>
                    <th>Tipo</th>
                    <th>Costo</th>
                    <th>Precio</th>
                    <th>Estado</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7}>Cargando inventario…</td>
                    </tr>
                  ) : null}
                  {!loading && !error && services.length === 0 ? (
                    <tr>
                      <td colSpan={7}>Todavía no hay servicios.</td>
                    </tr>
                  ) : null}
                  {!loading
                    ? services.map((service) => (
                        <tr key={service.id}>
                          <td>{service.nombre}</td>
                          <td>{specialtyLabel(service.especialidad_codigo)}</td>
                          <td>{service.tipo_cita === 'evaluacion' ? 'Evaluación' : 'Terapia'}</td>
                          <td>{formatAgendaPrecio(service.costo, service.moneda)}</td>
                          <td>{formatAgendaPrecio(service.precio, service.moneda)}</td>
                          <td>{service.activo ? 'Activo' : 'Inactivo'}</td>
                          <td>
                            <button type="button" className="admin-btn admin-btn--secondary" onClick={() => handleEdit(service)}>
                              Editar
                            </button>
                          </td>
                        </tr>
                      ))
                    : null}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdminInventoryPage;
